import React from 'react';
import { Link } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { useTheme } from '../context/ThemeContext';
import { 
  FaArrowLeft as FaArrowLeftBase, 
  FaCloudSun as FaCloudSunBase, 
  FaWind as FaWindBase, 
  FaTint as FaTintBase, 
  FaMapMarkerAlt as FaMapMarkerAltBase 
} from 'react-icons/fa';

// Fix for TS2786
const FaArrowLeft = FaArrowLeftBase as React.ElementType;
const FaCloudSun = FaCloudSunBase as React.ElementType;
const FaWind = FaWindBase as React.ElementType;
const FaTint = FaTintBase as React.ElementType;
const FaMapMarkerAlt = FaMapMarkerAltBase as React.ElementType;

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const WeatherForecastApp: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const data = {
    labels: ['Now', '2h', '4h', '6h', '8h', '10h'],
    datasets: [{
      label: 'Temp (°C)',
      data: [22, 23, 24, 21, 19, 18],
      borderColor: '#f59e0b',
      backgroundColor: 'rgba(245, 158, 11, 0.2)',
      fill: true,
      tension: 0.4,
    }]
  };

  return (
    <div className="min-h-screen bg-secondary pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/portfolio" className="inline-flex items-center text-secondary hover:text-accent mb-8 transition-colors">
          <FaArrowLeft className="mr-2" /> Back to Portfolio
        </Link>

        <div className="mb-12">
          <h1 className="text-4xl font-bold text-primary mb-4">Smart Weather Forecast</h1>
          <p className="text-xl text-secondary max-w-3xl">
            Hyper-local weather predictions combining OpenWeatherMap API data with historical trend analysis.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Weather Card UI */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-3xl p-8 text-white shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-white opacity-10 rounded-full blur-2xl"></div>
            
            <div className="flex justify-between items-start mb-8">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <FaMapMarkerAlt />
                  <span className="text-lg font-medium">San Francisco, CA</span>
                </div>
                <p className="text-blue-100 text-sm">Monday, 12 Oct</p>
              </div>
              <FaCloudSun size={48} className="text-yellow-300" />
            </div>

            <div className="flex items-end gap-4 mb-8">
              <h2 className="text-7xl font-bold">22°</h2>
              <p className="text-xl mb-2 text-blue-100">Partly Cloudy</p>
            </div>

            <div className="grid grid-cols-3 gap-4 bg-white/10 rounded-xl p-4 backdrop-blur-sm">
              <div className="text-center">
                <FaWind className="mx-auto mb-1" />
                <p className="text-xs text-blue-100">Wind</p>
                <p className="font-bold">12 km/h</p>
              </div>
              <div className="text-center border-l border-white/20">
                <FaTint className="mx-auto mb-1" />
                <p className="text-xs text-blue-100">Humidity</p>
                <p className="font-bold">48%</p>
              </div>
              <div className="text-center border-l border-white/20">
                <span className="block text-lg font-bold">85%</span>
                <p className="text-xs text-blue-100">Confidence</p>
              </div>
            </div>
          </div>

          {/* Technical Details */}
          <div className="space-y-6">
            <div className="bg-primary p-6 rounded-xl border border-primary">
              <h3 className="text-lg font-bold text-primary mb-4">Temperature Trend</h3>
              <Line 
                data={data} 
                options={{ 
                  responsive: true, 
                  scales: { 
                    y: { display: false }, 
                    x: { grid: { display: false } } 
                  },
                  plugins: { legend: { display: false } }
                }} 
              />
            </div>

            <div className="bg-primary p-6 rounded-xl border border-primary">
              <h3 className="text-lg font-bold text-primary mb-2">How it works</h3>
              <p className="text-secondary text-sm leading-relaxed">
                Unlike standard weather apps that just display API data, this application stores historical weather patterns for the user's specific location in a local database. 
                A lightweight regression model runs in the browser (using TensorFlow.js) to adjust the API forecast based on local micro-climate deviations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeatherForecastApp;